import { createContext, useContext, useEffect, useState, ReactNode } from 'react';

/** Wave 5 auth types — mirrors backend `User` model (minus password fields). */
export interface AuthUser {
  user_id: string;
  email: string;
  name: string;
  role: string;
  picture?: string | null;
  org_id?: string | null;
  has_password?: boolean;
  is_guest?: boolean;
  /** Wave 5 Phase 4 — capability strings the role can perform.
   *  Source of truth: `backend/domains/tenants/permissions.py::Capability`. */
  capabilities?: string[];
  /** Per-user lockout for sensitive admin sections (seeded restricted test admins). */
  restricted_admin?: boolean;
}

/** Wave 5 Phase 4 — role vocabulary shared with the backend enum.
 *  Mirrors `backend/domains/tenants/models.py::Role`. */
export const ROLES = ['owner', 'admin', 'developer', 'viewer', 'billing', 'auditor'] as const;
export type Role = typeof ROLES[number];

/** Convenience — check if the current user has a capability. */
export function hasCap(user: AuthUser | null, cap: string): boolean {
  if (!user) return false;
  return Array.isArray(user.capabilities) && user.capabilities.includes(cap);
}

interface AuthState {
  user: AuthUser | null;
  loading: boolean;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
// The backend URL is baked at Vite build time by import.meta.env.VITE_API_BASE.
// In prod that resolves to https://app.cloudgenie.co ; in dev to http://localhost:8001 .
// Fallback to the production backend host so id.cloudgenie.co (which
// doesn't run its own backend) can hit the API cross-origin with the
// shared `.cloudgenie.co` cookie.
const API_BASE = import.meta.env.VITE_API_BASE || 'https://app.cloudgenie.co';

async function fetchMe(): Promise<AuthUser | null> {
  try {
    const res = await fetch(`${API_BASE}/api/auth/me`, {
      credentials: 'include',
      headers: { Accept: 'application/json' },
    });
    if (res.status === 401) return null;
    if (!res.ok) return null;
    return (await res.json()) as AuthUser;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    setLoading(true);
    const me = await fetchMe();
    setUser(me);
    setLoading(false);
  };

  const logout = async () => {
    await fetch(`${API_BASE}/api/auth/logout`, { method: 'POST', credentials: 'include' });
    setUser(null);
  };

  useEffect(() => { refresh(); }, []);

  return (
    <AuthContext.Provider value={{ user, loading, refresh, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}

export { API_BASE };
